import { Box, IconButton, makeStyles } from '@material-ui/core';
import { Currency } from '@nemuru-eng/js-api-client';
import { Fragment, ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import {
  EditAutoPaymentMethodSummaryRowData,
  EditAutoPaymentMethodSummarySection,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditAutoPaymentMethodSummarySection';
import { FinancingReselectionWarning } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/FinancingReselectionWarning';
import {
  createFinancingLiveSummaryRows,
  createSpecsLiveSummaryRows,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/editAutoPaymentMethodLiveSummaryRows.map';
import { EditAutoPaymentMethodFormFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface EditAutoPaymentMethodLiveSummaryProps {
  showVehicleSummary: boolean;
  showFinancingSummary: boolean;
  initialItem: AutomobileFormItem;
  initialFinancing: FinancingLiveSummaryData;
  currency: Currency;
  showLiveSummary: boolean;
  requiresFinancingReselection?: boolean;
}

export const EditAutoPaymentMethodLiveSummary = ({
  showVehicleSummary,
  showFinancingSummary,
  initialItem,
  initialFinancing,
  currency,
  showLiveSummary,
  requiresFinancingReselection = false,
}: EditAutoPaymentMethodLiveSummaryProps) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();
  const { control } = useFormContext<EditAutoPaymentMethodFormFields>();

  const [brand, model, version, identificationNumber, plateNumber, odometer, price, registrationDate] = useWatch({
    control,
    name: [
      'item.brand',
      'item.model',
      'item.version',
      'item.identificationNumber',
      'item.plateNumber',
      'item.odometer',
      'item.price',
      'item.registrationDate',
    ],
  });

  const currentFinancing = useWatch({ control, name: 'financing' }) ?? initialFinancing;

  const specsRows = createSpecsLiveSummaryRows({
    initialItem,
    currentItem: {
      brand,
      model,
      version,
      identificationNumber,
      plateNumber,
      odometer,
      price,
      registrationDate,
    },
    currency,
  });

  const financingRows = createFinancingLiveSummaryRows({
    initialPrice: initialItem.price,
    currentPrice: price,
    initialFinancing,
    currentFinancing,
    currency,
    getTextIdTranslation,
    termEndAdornment: <Text tId="MONTHS" />,
  });

  const sections: {
    key: string;
    titleTextId: string;
    rows: EditAutoPaymentMethodSummaryRowData[];
    content?: ReactNode;
  }[] = [];

  if (showVehicleSummary) {
    sections.push({ key: 'vehicle', titleTextId: 'AUTO_DETAILS', rows: specsRows });
  }

  if (showFinancingSummary) {
    sections.push({
      key: 'financing',
      titleTextId: 'FINANCING_SUMMARY',
      rows: requiresFinancingReselection ? [] : financingRows,
      content: requiresFinancingReselection ? (
        <Box pb={2}>
          <FinancingReselectionWarning initialFinancing={initialFinancing} currency={currency} />
        </Box>
      ) : undefined,
    });
  }

  return (
    <Box
      overflow="hidden"
      style={{
        opacity: showLiveSummary ? 1 : 0,
        transition: 'opacity 0.6s ease-in-out',
        pointerEvents: showLiveSummary ? 'auto' : 'none',
      }}
      minHeight="610px"
    >
      <Box
        py={1}
        pb={2}
        px={3}
        bgcolor={palette.common.white}
        border={`1px solid ${palette.grey[200]}`}
        borderRadius={customBorderRadius.paper}
      >
        <Font variant="h4" color="title" style={{ fontWeight: 700 }}>
          <Text tId="LIVE_SUMMARY" />
        </Font>
        <Box mt={2} display="flex" flexDirection="column" gridGap={12}>
          {sections.map((section, index) => (
            <Fragment key={section.key}>
              {index > 0 && <CustomDivider />}
              <EditAutoPaymentMethodSummarySection titleTextId={section.titleTextId} rows={section.rows}>
                {section.content}
              </EditAutoPaymentMethodSummarySection>
            </Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

interface LiveSummaryCollapseIconProps {
  showLiveSummary: boolean;
  onToggle: () => void;
}

export const LiveSummaryCollapseIcon = ({ showLiveSummary, onToggle }: LiveSummaryCollapseIconProps) => {
  const { palette } = useCustomTheme();

  const useStyles = makeStyles({
    nested: {
      transition: '0.1s ease',
      backgroundColor: palette.grey[50],
      color: palette.getContrastText(palette.grey[50]),
    },
    button: {
      '&:hover': {
        backgroundColor: palette.grey[100],
      },
    },
  });

  const classes = useStyles();

  return (
    <IconButton
      size="small"
      className={`${classes.nested} ${classes.button}`}
      onClick={onToggle}
      style={{ border: `1px solid ${palette.grey[300]}` }}
    >
      <Icon
        style={{
          background: 'transparent',
          borderRadius: '50%',
          transform: showLiveSummary ? 'rotateY(180deg)' : 'none',
          transition: 'transform 0.6s ease',
        }}
        size={20}
      >
        chevron_left
      </Icon>
    </IconButton>
  );
};
